<template>
  <div
    ref="wrap"
    class="pdf-viewer">
    <div
      v-if="loading"
      class="text-center pa-6">
      <v-progress-circular
        indeterminate
        color="primary"
        :size="40"
      />
      <div class="mt-2 caption">Loading newsletter...</div>
    </div>

    <v-alert
      v-else-if="error"
      type="warning"
      text
      class="ma-3">
      {{ error }}
    </v-alert>

    <div
      v-show="!loading && !error"
      ref="pages"
      class="pdf-pages"/>
  </div>
</template>

<script>
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf'

pdfjsLib.GlobalWorkerOptions.workerSrc = process.env.BASE_URL + 'pdf.worker.min.js'

export default {
  props: {
    src: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      loading: true,
      error: '',
      pdf: null,
      renderId: 0,
      lastWidth: 0,
      resizeTimer: null
    }
  },

  watch: {
    src: 'load'
  },

  mounted () {
    this.load()
    window.addEventListener('resize', this.onResize)
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.onResize)
    clearTimeout(this.resizeTimer)
    this.renderId++
    if (this.pdf) this.pdf.destroy()
  },

  methods: {
    async load () {
      this.loading = true
      this.error = ''

      try {
        if (this.pdf) {
          await this.pdf.destroy()
          this.pdf = null
        }
        this.pdf = await pdfjsLib.getDocument({ url: this.src }).promise
        this.loading = false
        await this.$nextTick()
        await this.render()
      } catch (e) {
        console.error('PDF load error:', e)
        this.loading = false
        this.error = 'This newsletter could not be displayed here. Please use the Download PDF button instead.'
      }
    },

    async render () {
      const container = this.$refs.pages
      const width = this.$refs.wrap && this.$refs.wrap.clientWidth
      if (!this.pdf || !container || !width) return

      const id = ++this.renderId
      this.lastWidth = width
      container.innerHTML = ''

      const dpr = window.devicePixelRatio || 1

      for (let n = 1; n <= this.pdf.numPages; n++) {
        const page = await this.pdf.getPage(n)
        if (id !== this.renderId) return // a newer render started, or were destroyed

        const base = page.getViewport({ scale: 1 })
        const scale = (width / base.width) * dpr
        const viewport = page.getViewport({ scale })

        const canvas = document.createElement('canvas')
        canvas.width = Math.floor(viewport.width)
        canvas.height = Math.floor(viewport.height)
        canvas.className = 'pdf-page'
        container.appendChild(canvas)

        await page.render({
          canvasContext: canvas.getContext('2d'),
          viewport: viewport
        }).promise

        if (id !== this.renderId) return
      }
    },

    onResize () {
      clearTimeout(this.resizeTimer)
      this.resizeTimer = setTimeout(() => {
        if (this.$refs.wrap && this.$refs.wrap.clientWidth !== this.lastWidth) {
          this.render()
        }
      }, 250)
    }
  }
}
</script>

<style scoped>
.pdf-viewer {
  background: #eceff1;
  padding: 8px;
  border-radius: 4px;
  max-height: 80vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.pdf-pages >>> .pdf-page {
  display: block;
  width: 100%;
  height: auto;
  margin: 0 auto 8px;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
}
</style>
